<template>
  <section id="live-training-summary" class="summary-section bg-white text-dark py-10">
    <div class="container-fluid px-md-5">
      <div class="row mb-5 align-items-end" data-aos="fade-up">
        <div class="col-lg-8">
          <div class="d-flex align-items-center mb-2">
            <span class="live-indicator animate-pulse"></span>
            <span class="text-danger fw-bold ms-2 letter-spacing-5">INSCRIPCIONES ABIERTAS</span>
          </div>
          <h2 class="display-3 fw-900 m-0">Entrenamiento Presencial</h2>
          <p class="section-subtitle mt-3">
            Formación práctica para escenarios reales. Instrucción directa por operadores de élite.
          </p>
        </div>
        <div class="col-lg-4 text-lg-end pb-2">
          <RouterLink to="/live-training" class="btn-tactical-dark">
            Ver todos los cursos <i class="bi bi-geo-alt-fill ms-2"></i>
          </RouterLink>
        </div>
      </div>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-danger" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p class="mt-2 fw-bold letter-spacing-5">Sincronizando información...</p>
      </div>

      <div v-else class="row g-4">
        <div 
          v-for="(course, index) in featuredCourses" 
          :key="course.id || index" 
          class="col-12 col-xl-6"
          data-aos="fade-up"
          :data-aos-delay="index * 150"
        >
          <div class="op-card" @click="goToDetails(course.id)">
            <div class="op-img-wrap">
              <div 
                class="op-img" 
                :style="{ backgroundImage: `url(${course.image})` }"
              ></div>
              <div class="op-overlay"></div>
              
              <div class="op-badge-location">
                <i class="bi bi-crosshair me-2"></i> {{ course.location }}
              </div>
            </div>

            <div class="op-meta d-flex justify-content-between align-items-start pt-4 border-start border-danger border-5 ps-4">
              <div class="meta-content">
                <h3 class="op-title">{{ course.name }}</h3>
                <p class="op-full-location text-secondary mb-1">
                  {{ course.fullLocation }}
                </p>
                <div class="op-schedule fw-bold">
                  <i class="bi bi-calendar3 text-danger me-2"></i> {{ course.date }}
                </div>
              </div>
              <div class="meta-action text-end">
                <div class="op-price">${{ course.price }}</div>
                <button class="btn-enroll-now">
                  RESERVAR CUPO <i class="bi bi-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getLiveTrainingSummary } from '@/utils/api_backend';

const router = useRouter();
const featuredCourses = ref([]);
const loading = ref(true);

const fetchCourses = async () => {
  try {
    loading.value = true;
    const data = await getLiveTrainingSummary();
    // La data ya viene mapeada gracias al serializador de Django que definimos
    featuredCourses.value = data;
  } catch (error) {
    console.error("Error loading missions:", error);
  } finally {
    loading.value = false;
  }
};

const goToDetails = (id) => {
  if (id) {
    router.push({ name: 'CourseDetail', params: { id: id } });
  }
};

onMounted(() => {
  fetchCourses();
});
</script>

<style scoped>
.summary-section { background-color: #ffffff; overflow: hidden; }
.fw-900 { font-weight: 900; }
.letter-spacing-5 { letter-spacing: 5px; font-size: 0.8rem; }

/* Indicador Live */
.live-indicator {
  width: 10px; height: 10px;
  background-color: #ff3131;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(255, 49, 49, 0.8);
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .4; }
}

/* Operation Card */
.op-card { cursor: pointer; transition: transform 0.3s ease; }

.op-img-wrap {
  position: relative;
  height: 450px;
  overflow: hidden;
  background-color: #000;
}

.op-img {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: grayscale(30%) brightness(0.8);
  transition: transform 0.8s cubic-bezier(0.2, 0, 0.2, 1);
}

.op-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(0,0,0,0.5) 0%, transparent 50%);
}

.op-card:hover .op-img {
  transform: scale(1.05);
  filter: grayscale(0%) brightness(0.9);
}

.op-badge-location {
  position: absolute;
  top: 25px; right: 25px;
  background: #ff3131;
  color: #fff;
  padding: 8px 15px;
  font-weight: 900;
  font-size: 0.75rem;
  letter-spacing: 1px;
}

/* Metadata */
.op-title {
  font-weight: 900;
  font-size: 1.8rem;
  line-height: 1;
  margin-bottom: 5px;
}

.op-price {
  font-weight: 900;
  font-size: 2rem;
  color: #000;
  line-height: 1;
}

.btn-enroll-now {
  background: transparent;
  border: none;
  color: #ff3131;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.85rem;
  padding: 0;
  margin-top: 10px;
}

.btn-enroll-now:hover { letter-spacing: 2px; transition: 0.3s; }

/* Botón Header */
.btn-tactical-dark {
  display: inline-block;
  padding: 15px 35px;
  background: #000;
  color: #fff;
  text-decoration: none;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: 0.3s;
}

.btn-tactical-dark:hover { background: #ff3131; transform: translateY(-3px); }
</style>